import { useEffect, useRef, useState, createContext, useContext } from "react";
import { IconArrowNarrowLeft, IconArrowNarrowRight, IconX } from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useOutsideClick } from "../hooks/use-outside-click";
import { BlurImage } from "./BlurImage";
import MyBook from "../MyBook";
import { useLocation, useNavigate } from "react-router-dom";

interface CarouselProps {
  items: JSX.Element[];
  initialScroll?: number;
}

type Card = {
  src: string;
  title: string;
  edition: string;
  description: string;
  date: string;
  path: string;
};

export const CarouselContext = createContext<{
  onCardClose: (index: number) => void;
  currentIndex: number;
}>({
  onCardClose: () => { },
  currentIndex: 0,
});

export const Carousel = ({ items, initialScroll = 0 }: CarouselProps) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = initialScroll;
      checkScrollability();
    }
  }, [initialScroll]);

  const checkScrollability = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scrollLeft = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  const handleCardClose = (index: number) => {
    if (carouselRef.current) {
      const cardWidth = isMobile() ? 230 : 384;
      const gap = isMobile() ? 4 : 8;
      const scrollPosition = (cardWidth + gap) * (index + 1);
      carouselRef.current.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
      setCurrentIndex(index);
    }
  };

  const isMobile = () => {
    return window.innerWidth < 768;
  };

  return (
    <CarouselContext.Provider
      value={{ onCardClose: handleCardClose, currentIndex }}
    >
      <div className="relative w-full">
        <div
          className="flex w-full overflow-x-scroll overscroll-x-auto py-3 pb-10  scroll-smooth [scrollbar-width:none]"
          ref={carouselRef}
          onScroll={checkScrollability}
        >
          <div
            className="absolute right-0 z-[1000] h-auto w-[5%] overflow-hidden bg-gradient-to-l"
          ></div>

          <div
            className="flex flex-row justify-start gap-4 pl-4 max-w-7xl mx-auto"
          >
            {items.map((item, index) => (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 * index, ease: "easeOut" }}
                key={"card" + index}
                className="last:pr-[5%] md:last:pr-[33%] rounded-3xl"
              >
                {item}
              </motion.div>
            ))}
          </div>
        </div>
        <div className="flex justify-center gap-5 ">
          <button
            className="relative z-40 h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center disabled:opacity-50"
            onClick={scrollLeft}
            disabled={!canScrollLeft}
          >
            <IconArrowNarrowLeft className="h-6 w-6 text-gray-500" />
          </button>
          <button
            className="relative z-40 h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center disabled:opacity-50"
            onClick={scrollRight}
            disabled={!canScrollRight}
          >
            <IconArrowNarrowRight className="h-6 w-6 text-gray-500" />
          </button>
        </div>
      </div>
    </CarouselContext.Provider>
  );
};

export const Card = ({
  card,
  index,
  layout = false,
}: {
  card: Card;
  index: number;
  layout?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { onCardClose } = useContext(CarouselContext);
  const location = useLocation();
  const navigate = useNavigate();

  const editionParam = new URLSearchParams(location.search).get("edition");
  console.log(editionParam, "dskjhdksajfh");

  useEffect(() => {
    if (editionParam) {
      const timer = setTimeout(() => {
        const cardEditionNum = card.edition.match(/\d+/)?.[0];
        const paramEditionNum = editionParam.match(/\d+/)?.[0];
        console.log(cardEditionNum, paramEditionNum, "djkhfjdghjkfdkfhgjkfhf");

        if (cardEditionNum === paramEditionNum) {
          setOpen(true);
        }
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [card.edition, editionParam]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        handleClose();
      }
    }

    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useOutsideClick(containerRef, () => handleClose());

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    onCardClose(index);
    navigate(location.pathname, { replace: true });

  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <div className="fixed bg-white inset-0 h-screen z-50 overflow-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="bg-black/80 backdrop-blur-lg h-full w-full fixed inset-0"
            />
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              ref={containerRef}
              layoutId={layout ? `card-${card.title}` : undefined}
              className="md:w-[75vw] max-w-[95vw] max-h-[95vh] mx-auto bg-white dark:bg-neutral-900 z-[60] rounded-3xl relative flex flex-col items-center overflow-hidden"
            >
              <button
                className="sticky top-4 h-8 w-8 right-0 ml-auto bg-black dark:bg-white rounded-full flex items-center justify-center mb-2 z-75"
                onClick={handleClose}
              >
                <IconX className="h-6 w-6 text-neutral-100 dark:text-neutral-900" />
              </button>

              <div className="flex justify-center items-center w-full h-full p-2">
                <MyBook
                  fileUrl={card.path}
                />

              </div>
            </motion.div>

          </div>
        )}
      </AnimatePresence>
      <motion.button
        layoutId={layout ? `card-${card.title}` : undefined}
        onClick={handleOpen}
        whileHover={{ scale: 1.06 }}
        className="rounded-3xl bg-gray-100 dark:bg-neutral-900 h-96 w-60 md:h-[28rem] hover: md:w-[18rem] overflow-hidden flex flex-col items-start justify-start relative z-10 hover:shadow-[inset_0_0_20px_#0dd3ff] transition-all duration-300 ease-in-out"
      >
        <div className="absolute h-full top-0 inset-x-0 bg-gradient-to-b from-black/50 via-transparent to-transparent z-30 pointer-events-none" />
        <div className="relative z-40 p-4">
          <div className="flex flex-row justify-between items-start">
            <motion.p
              layoutId={layout ? `category-${card.edition}` : undefined}
              className="text-white text-xs font-medium bg-black p-1 rounded-lg"
            >
              {`${card.edition} edition  ${card.date}`}
            </motion.p>
          </div>
          <div className="flex flex-row items-center justify-center">
            <motion.p
              layoutId={layout ? `title-${card.edition}` : undefined}
              className="text-white text-xs md:text-sm font-normal text-left mt-2 uppercase"
            >
              {card.description}
            </motion.p>
          </div>
          <motion.p
            layoutId={layout ? `title-${card.edition}` : undefined}
            className="text-[#57c0af] text-xs md:text-xs font-extralight max-w-xs text-left mt-2"
          >
            Read More
          </motion.p>
        </div>
        <BlurImage
          src={card.src}
          alt={card.title}
          className="object-cover h-[100%] absolute z-10 inset-0 transition-opacity duration-400 hover:opacity-100 hover:blur-sm"
        />
      </motion.button>
    </>
  );
};
